import { Calendar } from 'lucide-react';
import SolutionPage from '../../components/SolutionPage';

export default function AppointmentCoordination() {
  return (
    <SolutionPage
      label="Solution"
      headline="Scheduling should not require back-and-forth for both sides."
      subheadline="The friction of coordinating appointments (the emails, the phone tag, the confirmations) consumes time and creates opportunities for things to go wrong."
      heroImage="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1920"
      icon={Calendar}
      problemTitle="Unorganized scheduling creates stress and lost opportunities."
      problemBody={[
        'For service-based businesses, the process of getting a customer from inquiry to confirmed appointment involves multiple steps, each one a potential failure point.',
        'Without a clear process, confirmations get missed, appointments are forgotten, follow-ups do not happen, and the customer experience feels disorganized even when the service itself is excellent.',
        'The cost is not just the occasional missed appointment. It is the ongoing friction that customers feel, the time staff spend on coordination, and the quiet erosion of the professional impression the business works hard to create.',
      ]}
      outcomes={[
        {
          title: 'Smoother intake from first inquiry',
          description:
            'Customers can express interest and begin the scheduling process without waiting for someone to get back to them.',
        },
        {
          title: 'Reliable confirmations and reminders',
          description:
            'Appointments are confirmed promptly and reminders are sent at the right time, reducing no-shows without requiring staff attention.',
        },
        {
          title: 'Consistent follow-up after appointments',
          description:
            'Post-appointment communication happens reliably, whether it is a thank-you, a follow-up question, or information the customer needs.',
        },
        {
          title: 'Less staff time spent on coordination',
          description:
            'When the coordination process runs consistently, your team can focus on delivering the service rather than managing the logistics around it.'
        },
      ]}
      whatWeDoTitle="We design scheduling workflows that handle coordination with care."
      whatWeDoBody={[
        'We look carefully at the full appointment journey, from the moment a customer first expresses interest to the follow-up after the appointment is complete.',
        'We identify where delays occur, where information is lost, and where the customer experience breaks down. Then we set up clear steps that address each of those points.',
        'The result is a process that feels smooth and professional for customers, and requires significantly less manual coordination from your team.'
      ]}
      whatYouGain={[
        'A clear, organized process from initial inquiry to confirmed appointment.',
        'Consistent confirmations and reminders that reduce no-shows.',
        'Consistent follow-up that does not require manual effort.',
        'Customers who feel well-cared for throughout the scheduling process.',
        'More time for your team to focus on delivering excellent service.',
        'Visibility into your schedule and incoming requests without chasing information.',
      ]}
      relatedSolutions={[
        { title: 'Customer Communication', href: '/solutions/customer-communication' },
        { title: 'Business Workflow Support', href: '/solutions/business-workflow' },
        { title: 'Internal Organization', href: '/solutions/internal-organization' },
      ]}
    />
  );
}
